import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CaretDown, Check } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"
import { getLocalizedShiftType } from "@/modules/shift-plan-overview/helpers/shift-plan-helper"
import { useShiftPlanFiltersStore } from "@/modules/shift-plan-overview/stores/useShiftPlanFiltersStore"

export const ShiftFilter = () => {
  const { t } = useTranslation()

  const shiftTypes = useShiftPlanFiltersStore((state) => state.filters.shiftTypes)
  const setShiftTypes = useShiftPlanFiltersStore((state) => state.actions.setShiftTypes)
  const availableShiftTypes = useShiftPlanFiltersStore((state) => state.availableShiftTypes)
  const isLoading = useShiftPlanFiltersStore((state) => state.isLoading)
  const count = shiftTypes.length

  const sortedOptions = useMemo(() => {
    return [...availableShiftTypes].sort((a, b) => {
      if (a === "MEDSUP") return -1
      if (b === "MEDSUP") return 1
      return a.localeCompare(b)
    })
  }, [availableShiftTypes])

  const handleCheckChange = (item: string) => {
    const index = shiftTypes.indexOf(item)
    const newShiftTypes = [...shiftTypes]

    if (index < 0) newShiftTypes.push(item)
    else newShiftTypes.splice(index, 1)

    setShiftTypes(newShiftTypes)
  }

  const handleReset = () => {
    setShiftTypes([])
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        disabled={isLoading}
        className={cn(
          "aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden hover:cursor-pointer",
          isLoading && "cursor-not-allowed!",
        )}>
        <Chip data-testid="shift-plan-shift-filter-dropdown" className="h-8 gap-0.5" checked={count > 0} type="button">
          <div className={`overflow-hidden transition-all duration-200 ${count > 0 ? "w-5" : "w-0"}`}>
            <Check size={20} />
          </div>
          <span className="px-1.5">{t("shiftPlanOverview.filters.shift") + (count > 0 ? ` (${count})` : ``)}</span>
          <CaretDown size={20} />
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        data-testid="shift-plan-shift-filter-content"
        className="absolute -top-1 grid gap-2 rounded-md border-none p-2 leading-5">
        <ScrollArea className="max-h-80">
          <div className="grid gap-0.5">
            {sortedOptions.map((item) => (
              <DropdownMenuCheckboxItem
                onSelect={(e) => e.preventDefault()}
                className="h-9"
                key={item}
                checked={shiftTypes.includes(item)}
                data-testid={`shift-shift-filter-${item.toLowerCase()}`}
                onCheckedChange={() => handleCheckChange(item)}>
                {getLocalizedShiftType(t, item)}
              </DropdownMenuCheckboxItem>
            ))}
          </div>
        </ScrollArea>
        <DropdownMenuSeparator className="bg-border -mx-2 my-0" />
        <div className="flex justify-end">
          <Button
            onClick={handleReset}
            data-testid="shift-plan-reset-shift-filter"
            variant="tertiary"
            className="h-9 rounded-full px-2 py-0 leading-5 font-medium">
            {t("common.button.reset")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
